import { ReactNode } from 'react';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { Skeleton } from './Skeleton';

export interface StatCardProps {
  title: string;
  value: number | string;
  icon?: ReactNode;
  iconBg?: string;
  iconColor?: string;
  valueColor?: string;
  isLoading?: boolean;
  className?: string;
  /** Position index used to stagger the entrance animation (delay = 0.05 * index). */
  index?: number;
}

export const StatCard = ({
  title,
  value,
  icon,
  iconBg = 'bg-primary-50',
  iconColor = 'text-primary-500',
  valueColor,
  isLoading = false,
  className,
  index = 0,
}: StatCardProps) => {
  const formattedValue =
    typeof value === 'number' ? value.toLocaleString() : value;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05 * index }}
      className={clsx('card p-5 flex gap-4 items-center', className)}
    >
      {/* Icon */}
      {icon && (
        <div className={clsx('rounded-lg p-2.5', iconBg, iconColor)}>
          {icon}
        </div>
      )}

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p className="text-sm text-[var(--color-text-secondary)] truncate">
          {title}
        </p>
        {isLoading ? (
          <Skeleton variant="rounded" height={28} width="60%" className="mt-1" />
        ) : (
          <p
            className={clsx(
              'mt-1 text-2xl font-semibold font-mono',
              valueColor || 'text-[var(--color-text-primary)]'
            )}
          >
            {formattedValue}
          </p>
        )}
      </div>
    </motion.div>
  );
};
